import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { TGetAccount } from "api/rpc/2024-04/facilityAdmin/client/accounts/accounts";
import { TCustomerType } from "redux/reducers/models/facility";
import { ILeague } from "redux/reducers/models/league";
import { ITournament } from "redux/reducers/models/tournament";
import { countActiveAccountsDrawerFilters } from "./AccountsFilterDrawer";
import "./accountsAppliedFilters.scss";

export type AccountsAppliedFilterKey =
  | "customer_type"
  | "cart_customer_type"
  | "league"
  | "tournament"
  | "has_default_payment_method"
  | "closed";

type Chip = {
  key: AccountsAppliedFilterKey;
  label: string;
};

type Props = {
  filters: TGetAccount;
  customerTypes: TCustomerType[];
  leagues: ILeague[];
  tournaments: ITournament[];
  onRemoveFilter: (key: AccountsAppliedFilterKey) => void;
  onClearAll: () => void;
};

function formatNames(names: string[], maxVisible: number): string {
  if (names.length <= maxVisible) {
    return names.join(", ");
  }
  const visible = names.slice(0, maxVisible).join(", ");
  return `${visible} +${names.length - maxVisible}`;
}

export default function AccountsAppliedFilters(props: Props) {
  const { filters, customerTypes, leagues, tournaments, onRemoveFilter, onClearAll } = props;
  const { t } = useTranslation();

  const chips = useMemo(() => {
    const next: Chip[] = [];

    if ((filters.customer_type_id?.length ?? 0) > 0) {
      const names = (filters.customer_type_id ?? []).map(id => {
        const type = customerTypes.find(customerType => customerType.id === id);
        return type?.title ?? String(id);
      });
      next.push({
        key: "customer_type",
        label: `${t("Customer Type")}: ${formatNames(names, 2)}`,
      });
    }

    if ((filters.cart_customer_type_id?.length ?? 0) > 0) {
      const names = (filters.cart_customer_type_id ?? []).map(id => {
        const type = customerTypes.find(customerType => customerType.id === id);
        return type?.title ?? String(id);
      });
      next.push({
        key: "cart_customer_type",
        label: `${t("Cart Type")}: ${formatNames(names, 2)}`,
      });
    }

    if (filters.league_id) {
      const league = leagues.find(item => item.id === filters.league_id);
      next.push({
        key: "league",
        label: `${t("League")}: ${league?.name ?? String(filters.league_id)}`,
      });
    }

    if (filters.tournament_id) {
      const tournament = tournaments.find(item => item.id === filters.tournament_id);
      next.push({
        key: "tournament",
        label: `${t("Tournament")}: ${tournament?.name ?? String(filters.tournament_id)}`,
      });
    }

    if (typeof filters.has_default_payment_method === "boolean") {
      next.push({
        key: "has_default_payment_method",
        label: `${t("Has Payment Method")}: ${
          filters.has_default_payment_method ? t("Yes") : t("No")
        }`,
      });
    }

    if (filters.closed !== false) {
      next.push({
        key: "closed",
        label: `${t("Closed")}: ${
          filters.closed === true ? t("Closed") : t("Any")
        }`,
      });
    }

    return next;
  }, [filters, customerTypes, leagues, tournaments, t]);

  if (countActiveAccountsDrawerFilters(filters) === 0) {
    return null;
  }

  return (
    <div className="accounts-applied-filters">
      <div className="accounts-applied-filters__chips">
        {chips.map(chip => (
          <div key={chip.key} className="accounts-applied-filters__chip">
            <span>{chip.label}</span>
            <button
              type="button"
              className="accounts-applied-filters__chip-remove"
              aria-label={t("Remove filter")}
              onClick={() => onRemoveFilter(chip.key)}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        ))}
        <button type="button" className="accounts-applied-filters__clear-all" onClick={onClearAll}>
          <FontAwesomeIcon icon={faXmark} />
          {t("Clear Filters")}
        </button>
      </div>
    </div>
  );
}
